// Import react.
import * as React from 'react';
// Import props.
import {
	ComparativePublicBuildingsSummaryProps,
	ComparativePolicyCostSummaryProps,
	ComparativePolicyInformationSummaryProps,
	ComparativePublicDwellingSummaryProps,
	FormPublicPolicyViewLandingProps,
	FormProps,
	ScrollbarProps,
} from '@app/props';
// Import hooks.
import {
	useFormMortgageesComparison,
	UseFormMortgageesComparisonResults,
} from '@app/hooks/view';

interface UseFormPublicPolicyViewLandingParameters {
	id: string;
	props: FormPublicPolicyViewLandingProps,
}

interface UseFormPublicPolicyViewLandingResults {
	applyFormProps: () => FormProps & ScrollbarProps;
	applyComparativePolicyInformationSummaryProps: () => ComparativePolicyInformationSummaryProps;
	applyComparativePublicDwellingSummaryProps: () => ComparativePublicDwellingSummaryProps;
	applyComparitivePublicBuildingsSummaryProps: () => ComparativePublicBuildingsSummaryProps;
	applyComparativePolicyCostSummaryProps: () => ComparativePolicyCostSummaryProps
	formMortgageesComparison: UseFormMortgageesComparisonResults;
	hasMortgagees: boolean;
	showBuildings: boolean;
	showDwelling: boolean;
	showPolicyCost: boolean;
}

const useFormPublicPolicyViewLanding = (params: UseFormPublicPolicyViewLandingParameters): UseFormPublicPolicyViewLandingResults => {

	// #region exported methods

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			formRef: params.props.formRef,
			contentRef: params.props.contentRef,
		};
	};

	/**
	 * Return props for the comparative policy information summary.
	 */
	const applyComparativePolicyInformationSummaryProps = (): ComparativePolicyInformationSummaryProps => {
		return {
			id: generateIdForComparativePolicyInformationSummary(),
			broadLineOfBusiness: params.props.broadLineOfBusinessCode,
			policyName: params.props.policyName,
			floodZone: params.props.floodZone,
			coveredPerils: params.props.coveredPerils,
			insuredInfo: params.props.insuredInfo,
			producerInfo: params.props.producerInfo,
			currentTermEffectiveDate: params.props.publicPolicyCurrentTermEffectiveDate,
			currentTermEndDate: params.props.publicPolicyCurrentTermEndDate,
			onPrintView: params.props.onPrintView,
			onViewPolicyPacket: params.props.onViewPolicyPacket,
		};
	}

	/**
	 * Returns props for the comparative dwelling summary. 
	 **/
	const applyComparativePublicDwellingSummaryProps = (): ComparativePublicDwellingSummaryProps => {
		return {
			id: generateIdForComparativePublicDwellingSummary(),
			building: params.props.buildingPersonal,
		};
	};

	/**
	 * Returns props for the comparative public buildings summary.
	 */
	const applyComparitivePublicBuildingsSummaryProps = (): ComparativePublicBuildingsSummaryProps => {
		return {
			id: generateIdForComparativeBuildingsSummary(),
			buildings: params.props.buildingsCommercial,
			areHeadersSticky,
		};
	}

	/**
	 * Returns props for the comparative policy cost summary. 
	 **/
	const applyComparativePolicyCostSummaryProps = (): ComparativePolicyCostSummaryProps => {
		return {
			id: generateIdForComparativePolicyCostSummary(),
			currentTermPolicyCost: params.props.publicPolicyCurrentTermCost,
			nextTermPolicyCost: params.props.publicPolicyNextTermCost,
			nextTermEffectiveDate: params.props.publicPolicyNextTermEffectiveDate,
			nextTermEndDate: params.props.publicPolicyNextTermEndDate,
			isPublicPolicyPaymentDue: params.props.isPublicPolicyPaymentDue,
			onMakePayment: params.props.onMakePayment,
			areHeadersSticky,
		};
	};

	// #endregion exported methods


	// #region private methods

	// #region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the comparative policy information summary. 
	 **/
	const generateIdForComparativePolicyInformationSummary = (): string => {
		return `${generateIdForForm()}-policyInformation`;
	}

	/**
	 * Generates id for the comparative public dwelling summary. 
	 **/
	const generateIdForComparativePublicDwellingSummary = (): string => {
		return `${generateIdForForm()}-dwellingSummary`;
	}

	/**
	 * Generates id for the comparative buildings summary. 
	 **/
	const generateIdForComparativeBuildingsSummary = (): string => {
		return `${generateIdForForm()}-buildingsSummary`;
	}

	/**
	 * Generates id for the comparative policy cost summary. 
	 **/
	const generateIdForComparativePolicyCostSummary = (): string => {
		return `${generateIdForForm()}-policyCost`;
	}

	/**
	 * Generates id for the mortgagees comparison form. 
	 **/
	const generateIdForMortgageesComparisonForm = (): string => {
		return `${generateIdForForm()}-mortgageesComparisonForm`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	const areHeadersSticky: boolean = React.useMemo(() => {
		return params.props.areHeadersSticky ?? false;
	}, [params.props.areHeadersSticky]);

	const formMortgageesComparison: UseFormMortgageesComparisonResults = useFormMortgageesComparison({
		id: generateIdForMortgageesComparisonForm(),
		props: {
			focusToTopOnMountIfReviewNotRequired: false,
			entityActionProps: params.props.mortgageeEntityActionProps,
			possibleStates: params.props.possibleStates,
			comparisonMortgageesSummary: params.props.comparisonMortgageesSummary,
			areHeadersSticky: areHeadersSticky,
		}
	});

	const hasMortgagees: boolean = React.useMemo(() => {
		return params.props.comparisonMortgageesSummary.comparisonEntitySummaries.length > 0;
	}, [params.props.comparisonMortgageesSummary]);

	const showBuildings: boolean = React.useMemo(() => {
		return params.props.buildingsCommercial.length > 0;
	}, [params.props.buildingsCommercial]);

	const showDwelling: boolean = React.useMemo(() => {
		return params.props.buildingPersonal !== undefined;
	}, [params.props.buildingPersonal]);

	const showPolicyCost: boolean = React.useMemo(() => {
		return params.props.publicPolicyCurrentTermCost !== undefined
			&& params.props.publicPolicyNextTermCost !== undefined;
	}, [params.props.publicPolicyCurrentTermCost, params.props.publicPolicyNextTermCost]);

	// #endregion variables

	return {
		applyFormProps,
		applyComparativePolicyInformationSummaryProps,
		applyComparativePublicDwellingSummaryProps,
		applyComparitivePublicBuildingsSummaryProps,
		applyComparativePolicyCostSummaryProps,
		formMortgageesComparison,
		hasMortgagees,
		showBuildings,
		showDwelling,
		showPolicyCost,
	};
}

// Export parameters.
export {
	UseFormPublicPolicyViewLandingParameters,
}

// Export results.
export {
	UseFormPublicPolicyViewLandingResults,
}

export default useFormPublicPolicyViewLanding;